import { useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { RootReducer } from 'redux/reducers';
import { setDsTabScrollPosition } from 'redux/mode/actions';

// Saves and restores scrollTop for a DS tab's scroll container.
// Attach to the element that has overflow-y: auto.
// Use restoreDelayMs when the scroll container has async content (e.g. iframes) that changes layout after load.
const useScrollMemory = (tabId: string | null, restoreDelayMs = 0) => {
	const dispatch = useDispatch();
	const savedScrollTop = useSelector((r: RootReducer) =>
		tabId ? r.mode.dsTabScrollPositions[tabId] : undefined
	);

	// Read saved value from a ref so the callback doesn't recreate when scroll events
	// update Redux. Without this, restoring scroll triggers a save → Redux update →
	// callback identity change → React re-calls ref(null) then ref(el) → cancels the
	// delayed restore and re-restores with the wrong (intermediate) value.
	const savedScrollTopRef = useRef(savedScrollTop);
	savedScrollTopRef.current = savedScrollTop;

	const cleanupRef = useRef<(() => void) | null>(null);

	// Ref callback: restore when attached, add scroll listener; cleanup when detached.
		// Handles conditionally rendered scroll containers (GuidelinesView, etc.).
	const setRef = useCallback(
		(el: HTMLDivElement | null) => {
			cleanupRef.current?.();
			cleanupRef.current = null;

			if (el && tabId) {
				const cleanups: (() => void)[] = [];
				const targetScrollTop = savedScrollTopRef.current;

				// Restore scroll position: apply immediately to avoid top flash, then re-apply after
				// delay if layout changes (e.g. async iframes in gallery previews).
				if (targetScrollTop !== undefined) {
					const apply = () => {
						el.scrollTop = targetScrollTop;
					};
					apply();
					if (restoreDelayMs > 0) {
						const id = setTimeout(apply, restoreDelayMs);
						cleanups.push(() => clearTimeout(id));
					}
				}

				// Save scroll position on scroll (debounced)
				const onScroll = debounce(() => {
					dispatch(setDsTabScrollPosition({ tabId, scrollTop: el.scrollTop }));
				}, 150);
				el.addEventListener('scroll', onScroll, { passive: true });
				cleanups.push(() => {
					onScroll.cancel();
					el.removeEventListener('scroll', onScroll);
				});

				cleanupRef.current = () => cleanups.forEach((fn) => fn());
			}
		},
		[tabId, dispatch, restoreDelayMs]
	);

	return setRef;
};

export default useScrollMemory;
